// src/components/common/Filter_store.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Filter_store.css";
import { useTranslation } from "react-i18next";

/**
 * 通用店铺/平台筛选（无搜索版；含“更多”下拉）
 *
 * props:
 * - loading?: boolean         // 右栏是否仍在加载可选项
 * - stores: string[]
 * - platforms: string[]
 * - value: { stores: string[], platforms: string[] }
 * - onChange: (next) => void
 * - className?: string
 * - storeVisible?: number     // 默认 12：主列表最多显示 12，其余进 More
 * - platformVisible?: number  // 默认 5：主列表最多显示 5，其余进 More
 */
export default function Filter_Store({
  loading = false,
  stores = [],
  platforms = [],
  value = { stores: [], platforms: [] },
  onChange = () => {},
  className = "",
  storeVisible = 8,
  platformVisible = 11,
}) {
  const { t } = useTranslation(); // ✅ 启用多语言钩子

  // 选中集合
  const selStoreSet = useMemo(() => new Set(value.stores || []), [value.stores]);
  const selPlatSet = useMemo(() => new Set(value.platforms || []), [value.platforms]);

  // 全量（去重 + 排序）
  const allStores = useMemo(() => [...new Set(stores)].sort(), [stores]);
  const allPlats = useMemo(() => [...new Set(platforms)].sort(), [platforms]);

  // 若父组件刚加载好可选项，自动把“选中集合中无效的项”收敛掉
  useEffect(() => {
    // ✅ 可选项还没加载出来时，不要动父组件（否则会把全局 filters 清空）
    if (allStores.length === 0 && allPlats.length === 0) return;
    const validStores = value.stores?.filter((s) => allStores.includes(s)) ?? [];
    const validPlats = value.platforms?.filter((p) => allPlats.includes(p)) ?? [];
    if (
      validStores.length !== (value.stores?.length || 0) ||
      validPlats.length !== (value.platforms?.length || 0)
    ) {
      onChange({ stores: validStores, platforms: validPlats });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStores.join("|"), allPlats.join("|")]);

  // 主列表 + 更多
  const storeMain = allStores.slice(0, storeVisible);
  const storeMore = allStores.slice(storeVisible);
  const platMain = allPlats.slice(0, platformVisible);
  const platMore = allPlats.slice(platformVisible);

  // 勾选切换
  const toggle = (kind, name) => {
    if (loading) return;
    const next = {
      stores: [...selStoreSet],
      platforms: [...selPlatSet],
    };
    const isStore = kind === "store";
    const set = isStore ? selStoreSet : selPlatSet;
    const key = isStore ? "stores" : "platforms";
    next[key] = set.has(name) ? [...set].filter((x) => x !== name) : [...set, name];
    onChange(next);
  };

  // 全选/全不选
  const selectAll = (kind, list, checked) => {
    if (loading) return;
    const next = {
      stores: [...selStoreSet],
      platforms: [...selPlatSet],
    };
    const key = kind === "store" ? "stores" : "platforms";
    next[key] = checked ? [...list] : [];
    onChange(next);
  };

  // 清空
  const clearAll = () => {
    if (loading) return;
    onChange({ stores: [], platforms: [] });
  };

  // 三态 all
  const storeAllRef = useRef(null);
  const platAllRef = useRef(null);
  useEffect(() => {
    if (storeAllRef.current) {
      storeAllRef.current.indeterminate =
        selStoreSet.size > 0 && selStoreSet.size < allStores.length;
    }
    if (platAllRef.current) {
      platAllRef.current.indeterminate =
        selPlatSet.size > 0 && selPlatSet.size < allPlats.length;
    }
  }, [selStoreSet, selPlatSet, allStores.length, allPlats.length]);

  const idSafe = (s) => s.replace(/\s+/g, "_");

  // “更多” 下拉开关 + 点击外部关闭
  const [openStoreMore, setOpenStoreMore] = useState(false);
  const [openPlatMore, setOpenPlatMore] = useState(false);
  const storeMoreRef = useRef(null);
  const platMoreRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (e.key === "Escape") {
        setOpenStoreMore(false);
        setOpenPlatMore(false);
        return;
      }
      if (
        storeMoreRef.current &&
        !storeMoreRef.current.contains(e.target) &&
        platMoreRef.current &&
        !platMoreRef.current.contains(e.target)
      ) {
        setOpenStoreMore(false);
        setOpenPlatMore(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onDoc);
    };
  }, []);

  // loading/空态
  if (loading) {
    return (
      <div className={`filters-panel ${className}`}>
        <div className="box-title">{t("Store")}</div>
        <div className="skeleton-line" />
        <div className="skeleton-line" />
        <div className="skeleton-line" />
        <div className="box-title" style={{ marginTop: 8 }}>
          {t("Platform")}
        </div>
        <div className="skeleton-line" />
        <div className="skeleton-line" />
      </div>
    );
  }
  if (!allStores.length && !allPlats.length) {
    return (
      <div className={`filters-panel ${className}`}>
        <div className="box-title">{t("Store")}</div>
        <div className="muted">{t("No options")}</div>
        <div className="box-title" style={{ marginTop: 8 }}>
          {t("Platform")}
        </div>
        <div className="muted">{t("No options")}</div>
      </div>
    );
  }

  return (
    <div className={`filters-panel ${className}`}>
      {/* Store */}
      <div className="store-box" ref={storeMoreRef}>
        <div className="box-title">{t("Stores")}</div>

        <div className="store-item all">
          <input
            ref={storeAllRef}
            type="checkbox"
            id="__allStores"
            disabled={loading || allStores.length === 0}
            checked={allStores.length > 0 && selStoreSet.size === allStores.length}
            onChange={(e) => selectAll("store", allStores, e.target.checked)}
          />
          <label htmlFor="__allStores">{t("All")}</label>
          <span className="count">
            {selStoreSet.size}/{allStores.length}
          </span>
        </div>

        {storeMain.map((store) => {
          const id = `store_${idSafe(store)}`;
          return (
            <div className="store-item" key={store}>
              <input
                type="checkbox"
                id={id}
                disabled={loading}
                checked={selStoreSet.has(store)}
                onChange={() => toggle("store", store)}
              />
              <label htmlFor={id}>{store}</label>
            </div>
          );
        })}

        {storeMore.length > 0 && (
          <div className="more-wrap">
            <button
              type="button"
              className="more-btn"
              disabled={loading}
              onClick={() => setOpenStoreMore((v) => !v)}
              aria-expanded={openStoreMore}
            >
              {t("More")} ({storeMore.length})
            </button>
            {openStoreMore && (
              <div className="more-menu">
                {storeMore.map((store) => {
                  const id = `store_more_${idSafe(store)}`;
                  return (
                    <div className="store-item" key={store}>
                      <input
                        type="checkbox"
                        id={id}
                        disabled={loading}
                        checked={selStoreSet.has(store)}
                        onChange={() => toggle("store", store)}
                      />
                      <label htmlFor={id}>{store}</label>
                    </div>
                  );
                })}
                <div className="menu-actions">
                  <button
                    type="button"
                    className="btn-outline"
                    onClick={() => setOpenStoreMore(false)}
                  >
                    {t("Done")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Platform */}
      <div className="store-box" ref={platMoreRef}>
        <div className="box-title">{t("Platform")}</div>

        <div className="store-item all">
          <input
            ref={platAllRef}
            type="checkbox"
            id="__allPlatforms"
            disabled={loading || allPlats.length === 0}
            checked={allPlats.length > 0 && selPlatSet.size === allPlats.length}
            onChange={(e) => selectAll("platform", allPlats, e.target.checked)}
          />
          <label htmlFor="__allPlatforms">{t("All")}</label>
          <span className="count">
            {selPlatSet.size}/{allPlats.length}
          </span>
        </div>

        {platMain.map((platform) => {
          const id = `plat_${idSafe(platform)}`;
          return (
            <div className="store-item" key={platform}>
              <input
                type="checkbox"
                id={id}
                disabled={loading}
                checked={selPlatSet.has(platform)}
                onChange={() => toggle("platform", platform)}
              />
              <label htmlFor={id}>{platform}</label>
            </div>
          );
        })}

        {platMore.length > 0 && (
          <div className="more-wrap">
            <button
              type="button"
              className="more-btn"
              disabled={loading}
              onClick={() => setOpenPlatMore((v) => !v)}
              aria-expanded={openPlatMore}
            >
              {t("More")} ({platMore.length})
            </button>
            {openPlatMore && (
              <div className="more-menu">
                {platMore.map((platform) => {
                  const id = `plat_more_${idSafe(platform)}`;
                  return (
                    <div className="store-item" key={platform}>
                      <input
                        type="checkbox"
                        id={id}
                        disabled={loading}
                        checked={selPlatSet.has(platform)}
                        onChange={() => toggle("platform", platform)}
                      />
                      <label htmlFor={id}>{platform}</label>
                    </div>
                  );
                })}
                <div className="menu-actions">
                  <button
                    type="button"
                    className="btn-outline"
                    onClick={() => setOpenPlatMore(false)}
                  >
                    {t("Done")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="filter-actions">
        <button
          type="button"
          className="btn-outline"
          disabled={loading}
          onClick={clearAll}
        >
          {t("Clear")}
        </button>
      </div>
    </div>
  );
}
